/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Icon, Modal } from '@scuf/common';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import './DataUploadCommon.scss';
import UploadTab from './UploadTab/UploadTab';
import { useFileUploadContext } from '../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { ApiContext } from '../../../context/ApiContext';

import { getActivetabClassName } from '../../shared/Utils/ManualUpload/ManualUploadUtils';
import ChooseServiceIcon from '../../../images/ChooseService.svg';
import PreviewTab from './PreviewTab/PreviewTab';
import {
  IValidationMessageDescriptor,
  useFileValidator
} from '../../shared/Utils/EquipmentSpaceMappingAndProperties/FileReaderUtils/FileValidator';
import _ from 'lodash';
import { ZLOAssociationUploadSettings } from '../../shared/Utils/Constants';
import { IAssociationDataValidations } from '../../shared/Utils/EquipmentSpaceMappingAndProperties/DataUtils/DataValidator';
import {
  IActionProgressStatus,
  useZLODataSubmitter,
  useZLODataValidator
} from '../ZLODataUtils/ZLODataHooks';

const Indexes = {
  UploadTabIndex: {
    label: 'Upload Data',
    value: 0,
    isLoader: false
  },
  PreviewTabIndex: {
    label: 'Preview',
    value: 1,
    isLoader: false
  },
  LoadingTabIndex: {
    label: 'Loading',
    value: 2,
    isLoader: true
  },
  ValidationInProgress: {
    label: 'Validating',
    value: 3,
    isLoader: true
  },
  SubmissionInProgress: {
    label: 'Submitting',
    value: 4,
    isLoader: true
  }
};

const requiredZLOSubHeaders = [
  'SpatialElementName',
  'ElementName',
  'ElementType',
  'ZLOState',
  'ZoneOptimizationSetback'
];

const eqSpD = descriptors.equipmentSpaceMapping;
const d = descriptors.energyManagement;
const z = descriptors.ZLOOnboardingConfig;

const ZLOUploadModal = () => {
  const [t] = useTranslation();
  const [state, dispatch] = useFileUploadContext();
  const { showZLOModal, zloFile } = state;
  const apiContext = useContext(ApiContext);

  const { onClickGotoSiteHomePage } = apiContext;

  const [ActiveIndex, setActiveIndex] = useState(Indexes.UploadTabIndex.value);
  const [validatedData, setValidatedData] = useState<
    IAssociationDataValidations[]
  >([]);

  // Initialize validation and submission hooks
  // - File Validation
  const {
    isFileValid,
    validateFile,
    clearFileValidator,
    validationMessages: fileValidationMsgs
  } = useFileValidator(ZLOAssociationUploadSettings);

  // - Data Validation Screen Flows
  const {
    allValid,
    errorMsgs: dataValErrorMsgs,
    valStatus,
    numErrors,
    validationStepMsg: dataValState,
    validateUpload
  } = useZLODataValidator(requiredZLOSubHeaders);

  // - Submission Screen Flows
  const {
    submissionStatus,
    submissionErrorMsg,
    submitData: submitZLOData
  } = useZLODataSubmitter();

  const _getValidationMessage = (m: IValidationMessageDescriptor) => {
    if (m.args) return t(m.d, m.args);
    return t(m.d);
  };

  useEffect(() => {
    if (zloFile) {
      validateFile(zloFile);
    } else {
      clearFileValidator();
    }
  }, [zloFile]);

  const dataValidator = async () => {
    const _validatedData = await validateUpload(zloFile);
    if (_validatedData) {
      setValidatedData(_validatedData);
    }
  };

  // To use incase allValid is incorrectly returned
  // const submitDisabled = useMemo(() => {
  //   let isSubmitDisabled = true;
  //   if (validatedData && validatedData.length > 0) {
  //     const invalidData = _.filter(validatedData, (a) => !a.allValid);
  //     return invalidData.length > 0;
  //   }
  //   return isSubmitDisabled;
  // }, [dataValErrorMsgs, validatedData]);

  const closeModal = () => {
    setActiveIndex(Indexes.UploadTabIndex.value);
    dispatch(FileUploadAction.hideZLOUploadModal());
    setValidatedData([]);
  };

  const renderActivetab = (tabIndex: number) => {
    switch (tabIndex) {
      case Indexes.UploadTabIndex.value:
        return renderUploadtab();
      case Indexes.PreviewTabIndex.value:
        return renderPreviewTab();
      case Indexes.LoadingTabIndex.value:
        return renderLoadingStatus();
      case Indexes.ValidationInProgress.value:
        return renderValidatingStatus();
      case Indexes.SubmissionInProgress.value:
        return renderSubmittingStatus();
      default:
        return renderUploadtab();
    }
  };

  const renderUploadtab = () => {
    return (
      <>
        {renderModalNavigation()}
        <UploadTab
          fileValidationMessages={_.map(
            fileValidationMsgs,
            _getValidationMessage
          )}
        />
      </>
    );
  };

  const renderPreviewTab = () => {
    return (
      <>
        {renderModalNavigation()}
        <PreviewTab
          validatedData={validatedData}
          errorMessages={_.map(dataValErrorMsgs, _getValidationMessage)}
          allValid={allValid}
          numErrors={numErrors}
        />
      </>
    );
  };

  const renderLoadingStatus = () => {
    return (
      <div className="zlo-loading-status">
        <div className="loading-spinner-wrapper">
          <Button
            className="zlo-upload-modal-loading zlo-modal-spinner"
            loading={true}
            disabled={true}
            content="Loading"
          />
        </div>
        <div className="loading-title">{t(d.LOADING)}</div>
        <div className="loading-subtitle">
          <></>
        </div>
        <hr className="hr-width loading-divider" />
      </div>
    );
  };

  const renderValidatingStatus = () => {
    return (
      <div className="zlo-validating-status">
        <div className="validating-spinner-wrapper">
          <Button
            size="small"
            className="zlo-upload-modal-validating zlo-modal-spinner"
            loading={true}
            disabled={true}
            content="Loading"
          />
        </div>
        <div className="validating-title">{t(z.VALIDATING_TITLE)}</div>
        <hr className="hr-width validating-divider" />
        <div className="validating-subtitle">
          {dataValState && _getValidationMessage(dataValState)}
        </div>
      </div>
    );
  };

  const renderSubmittingStatus = () => {
    return (
      <div className="zlo-submitting-status">
        <div className={`submitting-spinner-wrapper ${submissionStatus} }`}>
          {submissionStatus === IActionProgressStatus.completed ? (
            <Icon
              root="common"
              className="submit-success-check-icon"
              name="badge-check"
            />
          ) : submissionStatus === IActionProgressStatus.error ? (
            <Icon
              root="common"
              className="submit-error-info-icon"
              name="badge-info"
            />
          ) : (
            <Button
              className="zlo-upload-modal-submitting zlo-modal-spinner"
              loading={true}
              disabled={true}
              content="Loading"
            />
          )}
        </div>
        <div className="submitting-title">
          {submissionStatus === IActionProgressStatus.completed
            ? t(z.SUBMISSION_SUCCESS_TITLE)
            : submissionStatus === IActionProgressStatus.error
            ? t(z.SUBMISSION_ERROR_TITLE)
            : t(z.SUBMITTING_TITLE)}
        </div>
        <hr className="hr-width submitting-divider" />
        <div className="submitting-subtitle">
          {submissionStatus === IActionProgressStatus.completed
            ? t(z.SUBMISSION_SUCCESS_SUBTITLE)
            : submissionStatus === IActionProgressStatus.error
            ? _getValidationMessage(submissionErrorMsg)
            : t(z.SUBMITTING_SUBTITLE)}
        </div>
        {submissionStatus === IActionProgressStatus.completed &&
          onClickGotoSiteHomePage && (
            <div className="go-to-site-wrapper">
              <Button
                type="link"
                content={t(z.GOTO_SITE_HOME_PAGE)}
                onClick={() => onClickGotoSiteHomePage()}
              />
            </div>
          )}
      </div>
    );
  };

  const renderModalNavigation = () => {
    return (
      <div className="modal-tab-button">
        {Object.entries(Indexes).map(([key, obj]) => {
          let icon;
          if (obj.isLoader) return;
          const index = obj.value;
          if (index < ActiveIndex) {
            icon = (
              <div className="service-icon">
                <img src={ChooseServiceIcon} />
              </div>
            );
          } else if (index === ActiveIndex) {
            icon = <div className="custom-badge-upload blue">{index + 1}</div>;
          } else {
            icon = <div className="custom-badge-upload grey">{index + 1}</div>;
          }
          return (
            <Fragment key={key}>
              <div className={getActivetabClassName(ActiveIndex, index)}>
                <button
                  onClick={
                    () =>
                      index === Indexes.UploadTabIndex.value &&
                      setActiveIndex(Indexes.UploadTabIndex.value)
                    // only upload tab clickable
                  }
                  className="upload-step-button"
                >
                  {icon}
                  <span className="upload-tab-header">{obj.label}</span>
                </button>
              </div>
              {index < 1 && <div className="upload-buttons-separator"></div>}
            </Fragment>
          );
        })}
      </div>
    );
  };

  const onValidateClick = async () => {
    setActiveIndex(Indexes.ValidationInProgress.value);
    await dataValidator();
    setActiveIndex(Indexes.PreviewTabIndex.value);
  };

  const onSubmitClick = async () => {
    setActiveIndex(Indexes.SubmissionInProgress.value);
    await submitZLOData(validatedData);
  };

  const renderFooterButtons = () => {
    return (
      <div className="upload-modal-footer">
        {ActiveIndex == Indexes.UploadTabIndex.value && (
          <>
            <div>
              <Button
                className="upload-data-cancel upload-footer-button"
                type="secondary"
                onClick={closeModal}
              >
                {t(d.CANCEL)}
              </Button>
            </div>
            <div>
              <Button
                disabled={!isFileValid}
                style={{ marginLeft: 'auto' }}
                type="primary"
                onClick={async () => {
                  await onValidateClick();
                }}
              >
                {t(z.VALIDATE_DATA)}
              </Button>
            </div>
          </>
        )}
        {ActiveIndex == Indexes.PreviewTabIndex.value && (
          <>
            <div>
              <Button
                className="upload-data-cancel upload-footer-button"
                type="secondary"
                onClick={() => {
                  setActiveIndex(Indexes.UploadTabIndex.value);
                  setValidatedData([]);
                }}
              >
                {t(z.BACK_TO_UPLOAD)}
              </Button>
            </div>
            <div>
              <Button
                disabled={!allValid}
                style={{ marginLeft: 'auto' }}
                type="primary"
                onClick={async () => {
                  await onSubmitClick();
                }}
              >
                {t(d.SUBMIT)}
              </Button>
            </div>
          </>
        )}
        {ActiveIndex === Indexes.SubmissionInProgress.value &&
          (submissionStatus === IActionProgressStatus.completed ||
            submissionStatus === IActionProgressStatus.error) && (
            <>
              <div>
                <Button
                  className="upload-data-cancel upload-footer-button"
                  type="secondary"
                  onClick={closeModal}
                >
                  {t(d.CLOSE)}
                </Button>
              </div>
            </>
          )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Modal
        className="zlo-upload-modal"
        size="fullscreen"
        closeIcon={
          _.includes(
            [
              Indexes.LoadingTabIndex.value,
              Indexes.SubmissionInProgress.value,
              Indexes.ValidationInProgress.value
            ],
            ActiveIndex
          ) && submissionStatus !== IActionProgressStatus.completed
            ? false
            : true
        }
        onClose={closeModal}
        open={showZLOModal}
        closeOnDimmerClick={false}
      >
        <div className="upload-modal-header">{t(z.UPLOAD_MODAL_TITLE)}</div>
        <Modal.Content className="upload-modal-content">
          {renderActivetab(ActiveIndex)}
        </Modal.Content>
        {renderFooterButtons()}
      </Modal>
    </React.Fragment>
  );
};

export default ZLOUploadModal;
